import { z } from 'zod';
import { IANATimezoneSchema } from '../common/timezone.schema.js';
import { HomecareClientStatusSchema, HomecarePaymentModeSchema, RelationshipToAccountHolderSchema, } from './signup.schema.js';
// ---------- recipient (sub-entity under the account) ----------
/** Recipient shape returned to the account holder (GET /me/recipients
 *  + the recipient list embedded in GET /me). Geocode lat/lng are
 *  exposed so the portal can show "address verified" / "geocoding
 *  pending" affordances. */
export const RecipientResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    dateOfBirth: z.string().date().nullable(),
    relationshipToAccountHolder: RelationshipToAccountHolderSchema,
    serviceAddressLine1: z.string(),
    serviceAddressLine2: z.string().nullable(),
    serviceAddressCity: z.string(),
    serviceAddressProvince: z.string(),
    serviceAddressPostalCode: z.string(),
    /** IANA timezone of the service address. Drives Phase 8+ rate-engine
     *  shift-time-bucket math + Phase 10+ booking local-time display. */
    timezone: IANATimezoneSchema,
    /** True iff the address has been successfully geocoded. Drives the
     *  "geocoding pending" portal affordance + Phase-9+ matching
     *  eligibility. */
    geocoded: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** POST /api/v1/homecare-clients/me/recipients — add a recipient
 *  under the account holder's HomecareClient. Service geocodes the
 *  address synchronously (Mapbox); if geocoding fails the recipient
 *  is still created with null lat/lng + the operator can retry. */
export const RecipientCreateRequestSchema = z.object({
    name: z.string().min(1).max(120).trim(),
    dateOfBirth: z.string().date().nullable().optional(),
    relationshipToAccountHolder: RelationshipToAccountHolderSchema.default('SELF'),
    serviceAddressLine1: z.string().min(1).max(200).trim(),
    serviceAddressLine2: z.string().max(200).trim().nullable().optional(),
    serviceAddressCity: z.string().min(1).max(80).trim(),
    /** Ontario only at v1 (Q4 — single-province). Validated against the
     *  literal 'ON' in the controller; not parametrised in the schema so
     *  the API contract stays explicit. */
    serviceAddressProvince: z.string().min(2).max(2).default('ON'),
    serviceAddressPostalCode: z.string().min(6).max(10).trim(),
    /** IANA timezone. Defaults to Eastern; account-holder picks
     *  America/Atikokan for Kenora-area recipients. */
    timezone: IANATimezoneSchema.default('America/Toronto'),
});
/** PATCH /api/v1/homecare-clients/me/recipients/{id} — partial update.
 *  Address fields cause re-geocoding on the service side. */
export const RecipientPatchRequestSchema = z.object({
    name: z.string().min(1).max(120).trim().optional(),
    dateOfBirth: z.string().date().nullable().optional(),
    relationshipToAccountHolder: RelationshipToAccountHolderSchema.optional(),
    serviceAddressLine1: z.string().min(1).max(200).trim().optional(),
    serviceAddressLine2: z.string().max(200).trim().nullable().optional(),
    serviceAddressCity: z.string().min(1).max(80).trim().optional(),
    serviceAddressProvince: z.string().min(2).max(2).optional(),
    serviceAddressPostalCode: z.string().min(6).max(10).trim().optional(),
    timezone: IANATimezoneSchema.optional(),
});
/** DELETE /api/v1/homecare-clients/me/recipients/{id} — soft-archive.
 *  No body; sets `archived_at = now()`. UI hides from the picker;
 *  past bookings (Phase 10+) keep their reference. */
export const RecipientArchiveResponseSchema = z.object({
    ok: z.literal(true),
});
// ---------- account (GET /me) ----------
/** GET /api/v1/homecare-clients/me — the signed-in account holder's
 *  view of their HomecareClient + non-archived recipients. */
export const HomecareMeResponseSchema = z.object({
    id: z.string().uuid(),
    paymentMode: HomecarePaymentModeSchema,
    /** True iff a Stripe Customer object has been created. False is
     *  the soft-degrade state ("Stripe customer pending"); the portal
     *  surfaces a "complete payment setup" affordance. */
    hasStripeCustomer: z.boolean(),
    /** True iff a default payment method has been added on the Stripe
     *  Customer. Required for first booking when paymentMode=STRIPE. */
    hasDefaultPaymentMethod: z.boolean(),
    languagePreference: z.string(),
    status: HomecareClientStatusSchema,
    suspensionReason: z.string().nullable(),
    /** Only non-archived recipients (`archived_at IS NULL`). */
    recipients: z.array(RecipientResponseSchema),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=self.schema.js.map